/**
 * @fileoverview Organism for complete chat management in the Inbox.
 * Orchestrates the visualization, sending and receiving of messages, integration of notes, audio and fast answers.
 * Implements optimized architecture with layer separation, memoization, integration of hooks and use of TailwindCSS.
 * Subscription to sharingInformationService: {@link useInboxChatSharingInformationUI}.
 */
import React, { memo, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ChatAttachMenuMC from './ChatAttachMenuMC';
import useChatQuickActionsUI from '../../../hooks/inbox/ui/useChatQuickActionsUI';
import useChatComponentUI from '../../../hooks/inbox/ui/useChatComponentUI';
import QuickActionsCarouselMC from '../../../components/QuickActionsCarouselMC';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import useChatComponentActions from '../../../hooks/inbox/actions/useChatComponentActions';
import useFastAnswerActions from '../../../hooks/inbox/actions/useFastAnswerActions';
import useProductSelectionActions from '../../../hooks/inbox/actions/useProductSelectionActions';
import EmojisContainer from '../../../../components/WhatsApp/EmojisContainer';
import ChatActionsBarMC from './ChatActionsBarMC';
import AttachedProductMC from './AttachedProductMC';
import AttachedFastAnswerMC from './AttachedFastAnswerMC';
import IsolatedChatInputMC from './IsolatedChatInputMC';
import useOrderActionSender from '../../../hooks/useOrderActionSender';
import useSelectedConversation from '../../../hooks/shared/useSelectedConversation';
import FastAnswersShortcutWrapperMC from './FastAnswersShortcutWrapperMC';
import MessageReplyPreviewMC from './MessageReplyPreviewMC';
import useReplyMessageSubscription from '../../../hooks/inbox/useReplyMessageSubscription';
import useInboxChatSharingInformationUI from '../../../hooks/inbox/ui/useInboxChatSharingInformationUI';
import COMMENT_THREAD_PLATFORMS from '../../../hooks/inbox/constants/commentThreadPlatforms';
import { isTikTokPlatform } from '../../../hooks/inbox/constants/platformMessageRestrictions';
import useMessageConsolidation from '../../../hooks/inbox/useMessageConsolidation';
import { isWhatsapp } from '../../../../util/messageUtil';

/**
 * MAX_CHARS - Maximum number of characters allowed in the chat.
 */
const MAX_CHARS = 4000;

/** z-index alto para tooltips de acciones rápidas (encima de overlays inbox móvil ~40–70) */
const QuickActionsTooltipZStyle = { zIndex: 10050 };

const ChatComponentMC = memo(({ openModal, isChatClosed }) => {
  const { t } = useTranslation('InboxMC');
  const conversation = useSelectedConversation();
  const retailerInfo = useSelector((state) => state.retailerUsersReducer.retailer_info);

  // UI Layer: visual state and centralized handlers
  const {
    openAttachMenu,
    setOpenAttachMenu,
    handleAttachMenuClick,
    showEmojiPicker,
    closeEmojiPicker,
    toggleEmojiPicker,
    textareaRef,
    selectedFastAnswer,
    setSelectedFastAnswer,
    selectedProduct,
    setSelectedProduct
  } = useChatComponentUI();

  const { replyMessage, clearReplyMessage } = useReplyMessageSubscription();

  const { onSendPdf, onSendPaymentLink } = useOrderActionSender({
    conversation,
    isChatClosed
  });

  // Fast Answer Actions Layer: complete management of fast answers
  const fastAnswerActions = useFastAnswerActions({
    textareaRef,
    conversation,
    setSelectedFastAnswer,
    selectedFastAnswer,
    replyMessage,
    clearReplyMessage
  });

  // Product Selection Layer: management of selected products from ProductSelection
  const productActions = useProductSelectionActions({
    conversation,
    textareaRef,
    selectedProduct,
    setSelectedProduct,
    replyMessage,
    clearReplyMessage
  });

  // Message Consolidation Layer (strictly for WhatsApp channels)
  const isWhatsappPlatform = isWhatsapp(conversation?.platform);
  const isConsolidationRuleActive = Boolean(retailerInfo?.consolidate_agent_messages) && isWhatsappPlatform;

  const handleSubmitRef = React.useRef(null);

  const handleFlushMessage = useCallback((textToSend, targetConvId) => {
    const convId = targetConvId || conversation?.id;
    if (!convId) return;
    // Dispatch text message
    handleSubmitRef.current?.('text', { message: textToSend }, convId);
  }, [conversation]);

  const consolidation = useMessageConsolidation({
    conversationId: conversation?.id,
    isRuleActive: isConsolidationRuleActive,
    onFlush: handleFlushMessage
  });

  // Actions Layer: decoupled business handlers
  const { handleSubmit, handleSendMessage, insertEmoji, handleKeyDown } = useChatComponentActions({
    conversation,
    setOpenAttachMenu,
    textareaRef,
    productActions,
    fastAnswerActions,
    replyMessage,
    clearReplyMessage,
    handleIncomingText: consolidation.handleIncomingText,
    flushConsolidatedMessage: consolidation.flushConsolidatedMessage
  });

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  useInboxChatSharingInformationUI({
    textareaRef,
    openModal,
    handleSubmit,
    setOpenAttachMenu,
    platform: conversation?.platform,
    flushConsolidatedMessage: consolidation.flushConsolidatedMessage
  });

  const quickActions = useChatQuickActionsUI({
    handleAttachMenuClick,
    openModal,
    platform: conversation?.platform,
    onSendPdf,
    onSendPaymentLink,
    conversation
  });

  // Automatically clear note when changing the conversation and focus the textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = '';
      textareaRef.current.focus();
    }
  }, [textareaRef]);

  useEffect(() => {
    closeEmojiPicker();
  }, [conversation?.id, closeEmojiPicker]);

  // Debug tracking optimized - use memoized stable variables
  const debugProps = useMemo(
    () => ({
      conversationId: conversation?.id,
      platform: conversation?.platform,
      openModalRef: openModal,
      openAttachMenu,
      selectedFastAnswer,
      onSendPdf,
      onSendPaymentLink,
      selectedProductId: selectedProduct?.id
    }),
    [
      conversation?.id,
      conversation?.platform,
      openModal,
      openAttachMenu,
      selectedFastAnswer,
      selectedProduct,
      onSendPdf,
      onSendPaymentLink
    ]
  );

  useTraceUpdate('ChatComponentMC', debugProps, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-wrapper tw-p-0 tw-w-full tw-overflow-visible">
      <div className="tw-relative">
        <div className="tw-w-full tw-mb-2 tw-flex-1 tw-min-w-0">
          <MessageReplyPreviewMC message={replyMessage} onClose={clearReplyMessage} />
          <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl tw-flex tw-flex-col tw-items-stretch tw-w-full tw-flex-1 tw-min-w-0 tw-relative">
            {showEmojiPicker && (
              <>
                <div
                  className="tw-fixed tw-inset-0 tw-bg-transparent tw-z-[10049]"
                  onClick={closeEmojiPicker}
                  aria-label={t('chat.closeEmojiPicker')}
                  tabIndex={-1}
                />
                <div className="tw-absolute tw-right-0 tw-bottom-0 tw-z-[10050]">
                  <EmojisContainer insertEmoji={insertEmoji} />
                </div>
              </>
            )}

            <FastAnswersShortcutWrapperMC textareaRef={textareaRef} />

            <AttachedProductMC
              product={selectedProduct}
              onRemoveProduct={productActions.removeProductImage}
            />

            <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center">
              <IsolatedChatInputMC
                ref={textareaRef}
                setSelectedFastAnswer={setSelectedFastAnswer}
                setSelectedProduct={setSelectedProduct}
                handleKeyDown={handleKeyDown}
                maxChars={MAX_CHARS}
              />
              <AttachedFastAnswerMC
                fastAnswer={selectedFastAnswer}
                onRemoveFastAnswer={fastAnswerActions.onRemoveFastAnswer}
              />
              <ChatActionsBarMC
                textareaRef={textareaRef}
                onToggleEmojiPicker={toggleEmojiPicker}
                onSendMessage={handleSendMessage}
                maxChars={MAX_CHARS}
                platform={conversation?.platform}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="tw-flex tw-items-center tw-mb-2 tw-relative">
        <QuickActionsCarouselMC
          actions={quickActions}
          maxVisible={
            COMMENT_THREAD_PLATFORMS.has(conversation?.platform) && !isTikTokPlatform(conversation?.platform)
              ? 1
              : quickActions.length - 1
          }
        />
        {openAttachMenu && (
          <ChatAttachMenuMC
            openModal={openModal}
            setOpenAttachMenu={setOpenAttachMenu}
            handleSubmit={handleSubmit}
            onSendPdf={onSendPdf}
            onSendPaymentLink={onSendPaymentLink}
            conversation={conversation}
            platform={conversation?.platform}
          />
        )}
      </div>
      <ReactTooltip
        place="top"
        anchorSelect=".send-icon-tooltip"
        className="!tw-z-[10050]"
        style={QuickActionsTooltipZStyle}
      />
    </div>
  );
});

ChatComponentMC.displayName = 'ChatComponentMC';

export default ChatComponentMC;
